import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { CurrentVariantType, ISavedModifier } from '../_types';
import { ICartItem, IRedux } from '../_store/types';
import { DECREMENT, INCREMENT, REMOVE } from '../_store/cart/cart.slice';
import useGTM from './useGTM';
import { useActiveBrand } from './useActiveBrand';

export default function useCartProduct(
  productId?: string,
  selectedModifiers?: ISavedModifier[],
  selVariants?: CurrentVariantType
) {
  const [quantity, setQuantity] = useState(1);

  const dispatch = useDispatch();
  const { sendGTM } = useGTM();

  const getConcatenatedIds = (
    items: { variant_id?: string; modifier_id?: string }[],
    key: 'variant_id' | 'modifier_id'
  ): string => {
    return items
      ?.map((item) => item[key])
      ?.sort()
      ?.join('_');
  };

  const onCheckProduct = useCallback(
    (productById?: ICartItem) => {
      if (productById) {
        const productVariants =
          selVariants && productById?.variants
            ? getConcatenatedIds(
              Object.values(productById.variants),
              'variant_id'
            )
            : '';

        let myVariants = '';
        if (selVariants) {
          let selVariantsArr = Object.values(selVariants).map((item) => ({
            variant_id: item?.variant_id,
          }));
          if (selVariantsArr) {
            myVariants = getConcatenatedIds(selVariantsArr, 'variant_id');
          }
        }
        const modifiersMatch =
          selectedModifiers &&
          productById.order_modifiers.length === selectedModifiers.length &&
          productById.order_modifiers.every(
            (mod: ISavedModifier, index: number) =>
              mod.modifier_id === selectedModifiers[index].modifier_id &&
              mod.modifier_quantity ===
              selectedModifiers[index].modifier_quantity
          );

        if (
          (!selVariants || productVariants === myVariants) &&
          (!selectedModifiers ||
            selectedModifiers?.length === 0 ||
            modifiersMatch)
        ) {
          return true;
        }
      }
      return false;
    },
    [selectedModifiers, productId, selVariants]
  );

  const shipperId = useActiveBrand()

  // Cart state'ni select qilish
  const cartState = useSelector(
    (state: IRedux) => state.cart?.cart,
  );
  const cart = cartState?.[shipperId]?.products || [];

  const productInCart = useMemo(() => {
    if (!productId) {
      console.log('Product ID is undefined');
      return null
    }

    const found = cart.find(
      (item) => {
        if (item.product_id === productId) {
          return onCheckProduct(item);
        } else {
          return false;
        }
      }
    );
    return found || null;
  }, [cart, productId, onCheckProduct]);

  const onRemove = () => {
    if (productInCart) {
      sendGTM('remove_from_cart', {
        cart: [productInCart],
      });
      const productWithBrand = cart.find(
        (item) =>
          item.key === productInCart.key
      );
      if (productWithBrand?.shipper_id) {
        dispatch(
          REMOVE({ shipper_id: productWithBrand.shipper_id, key: productInCart.key })
        );
      }
      setQuantity(1);
    }
  };

  const onIncrease = () => {
    if (productInCart?.quantity) {
      const productWithBrand = cart.find(
        (item) =>
          item.key === productInCart.key
      );
      if (productWithBrand?.shipper_id) {
        dispatch(
          INCREMENT({ shipper_id: productWithBrand.shipper_id, key: productInCart.key })
        );
      }
    } else {
      setQuantity((prevState) => ++prevState);
    }
  };

  const onDecrease = () => {
    if (productInCart?.quantity) {
      if (productInCart.quantity > 1) {
        const productWithBrand = cart.find(
          (item) =>
            item.key === productInCart.key
        );
        if (productWithBrand?.shipper_id) {
          dispatch(
            DECREMENT({
              shipper_id: productWithBrand.shipper_id,
              key: productInCart.key,
            })
          );
        }
      } else {
        onRemove();
      }
    } else {
      if (quantity > 1) {
        setQuantity((prevState) => --prevState);
      } else {
        setQuantity(1);
      }
    }
  };

  return {
    quantity: productInCart?.quantity || quantity,
    onRemove,
    onIncrease,
    onDecrease,
    setQuantity,
    productInCart: productInCart || null,
  };
}
